import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import MyAccountCard from './MyAccountCard';
import MyAccountPageShell from './MyAccountPageShell';
import DataTable from './DataTable';
import { LoadingBar, PageShellLoading, TableSkeleton } from './MyAccountLoadingStates';
import { accountActionIconGroupStyle, accountActionIconStyle } from './accountActionIconStyles';

interface TeamMember {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    company: string;
    role: string;
}

interface Role {
    id: number;
    name: string;
}

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: string;
}

interface TeamManagementProps {
    wekaPagePermissions?: Record<string, boolean>;
}

const TeamManagement: React.FC<TeamManagementProps> = ({ wekaPagePermissions = {} }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const editId = searchParams.get('edit');

    const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: ''
    });
    const restNonce = (window as any).wpApiSettings?.nonce || (window as any).wekaApiSettings?.nonce || '';

    const getApiErrorMessage = (result: any, fallback: string): string => {
        if (!result) {
            return fallback;
        }

        if (typeof result.message === 'string' && result.message.trim() !== '') {
            return result.message;
        }

        if (result.data && typeof result.data.message === 'string' && result.data.message.trim() !== '') {
            return result.data.message;
        }

        if (typeof result.data === 'string' && result.data.trim() !== '') {
            return result.data;
        }

        return fallback;
    };

    const roleOptions = useMemo(() => {
        if (roles.length > 0) {
            return roles.map((role) => role.name);
        }
        return ['admins'];
    }, [roles]);
    const canAddTeamMember = wekaPagePermissions.add_team_member !== false;
    const canEditTeamMember = wekaPagePermissions.edit_team_member !== false;
    const canDeleteTeamMember = wekaPagePermissions.delete_team_member !== false;

    const resetForm = () => {
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            role: roleOptions[0] || 'admins'
        });
    };

    useEffect(() => {
        Promise.all([loadTeamMembers(), loadRoles()]).finally(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (!editId) {
            resetForm();
            setShowForm(false);
            return;
        }

        const memberId = Number(editId);
        if (!Number.isFinite(memberId)) {
            setError('Invalid team member id.');
            return;
        }

        const member = teamMembers.find((item) => item.id === memberId);
        if (!member) {
            return;
        }

        setFormData({
            firstName: member.first_name || '',
            lastName: member.last_name || '',
            email: member.email || '',
            phone: member.phone || '',
            role: member.role || roleOptions[0] || 'admins'
        });
        setShowForm(true);
    }, [editId, teamMembers, roleOptions]);

    useEffect(() => {
        if (!editId) {
            return;
        }

        if (!canEditTeamMember) {
            setSearchParams({}, { replace: true });
            setShowForm(false);
            setError('Editing team members is disabled for this page.');
        }
    }, [editId, canEditTeamMember, setSearchParams]);

    useEffect(() => {
        if (!formData.role && roleOptions.length > 0) {
            setFormData((prev) => ({ ...prev, role: roleOptions[0] }));
        }
    }, [roleOptions, formData.role]);

    useEffect(() => {
        if (!showForm) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [showForm]);

    useEffect(() => {
        if (!showForm) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !submitting) {
                cancelEdit();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [showForm, submitting]);

    const loadTeamMembers = async () => {
        try {
            const response = await fetch('/wp-json/weka/v1/team/list', {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'X-WP-Nonce': restNonce
                }
            });

            const result = await response.json();
            if (result.success) {
                setTeamMembers(result.data || []);
            } else {
                setError(getApiErrorMessage(result, 'Failed to load team members'));
            }
        } catch (err) {
            setError('An error occurred while loading team members');
        }
    };

    const loadRoles = async () => {
        try {
            const response = await fetch('/wp-json/weka/v1/team/roles', {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'X-WP-Nonce': restNonce
                }
            });

            const result = await response.json();
            if (result.success) {
                setRoles(result.data || []);
            }
        } catch (err) {
            // Keep UI usable with fallback role option.
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const isEditMode = Boolean(editId);
        if (isEditMode && !canEditTeamMember) {
            setError('Editing team members is disabled for this page.');
            return;
        }
        if (!isEditMode && !canAddTeamMember) {
            setError('Adding team members is disabled for this page.');
            return;
        }

        setSubmitting(true);
        setError(null);
        setSuccess(null);

        try {
            const endpoint = isEditMode && editId
                ? `/wp-json/weka/v1/team/${encodeURIComponent(editId)}`
                : '/wp-json/weka/v1/team';

            const response = await fetch(endpoint, {
                method: isEditMode ? 'PUT' : 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': restNonce
                },
                body: JSON.stringify({
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.email,
                    phone: formData.phone,
                    role: formData.role
                })
            });

            const result = await response.json();
            if (!response.ok || !result.success) {
                setError(getApiErrorMessage(result, 'Failed to save team member'));
                return;
            }

            setSuccess(result.data?.message || 'Team member saved successfully');
            await loadTeamMembers();

            if (isEditMode) {
                setSearchParams({}, { replace: true });
            }

            resetForm();
            setShowForm(false);
        } catch (err) {
            setError('An error occurred while saving team member');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (memberId: number, memberEmail: string) => {
        if (!canDeleteTeamMember) {
            setError('Deleting team members is disabled for this page.');
            return;
        }

        if (!confirm(`Are you sure you want to delete ${memberEmail}?`)) {
            return;
        }

        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(`/wp-json/weka/v1/team/${memberId}`, {
                method: 'DELETE',
                credentials: 'same-origin',
                headers: {
                    'X-WP-Nonce': restNonce
                }
            });

            const result = await response.json();
            if (!response.ok || !result.success) {
                setError(getApiErrorMessage(result, 'Failed to delete team member'));
                return;
            }

            setSuccess(result.data?.message || 'Team member deleted successfully');
            if (editId === String(memberId)) {
                setSearchParams({}, { replace: true });
                setShowForm(false);
            }
            await loadTeamMembers();
        } catch (err) {
            setError('An error occurred while deleting team member');
        }
    };

    const handleEdit = (memberId: number) => {
        if (!canEditTeamMember) {
            setError('Editing team members is disabled for this page.');
            return;
        }
        setError(null);
        setSuccess(null);
        setSearchParams({ edit: String(memberId) }, { replace: false });
        setShowForm(true);
    };

    const cancelEdit = () => {
        setSearchParams({}, { replace: true });
        setError(null);
        setSuccess(null);
        resetForm();
        setShowForm(false);
    };

    if (loading) {
        return (
            <PageShellLoading
                showMeta={true}
                showAction={true}
                titleWidth="28%"
                subtitleWidth="54%"
                metaWidth="120px"
                actionWidth="164px"
            >
                <TableSkeleton columns={5} rows={5} />
                <MyAccountCard
                    variant="page"
                    slug="team-management-loading-form"
                    className="mt-4 weka-my-account-page__section-card"
                    wrapContent={false}
                >
                        <LoadingBar height="20px" width="24%" style={{ marginBottom: '16px' }} />
                        <div className="row g-3">
                            <div className="col-md-6"><LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} /></div>
                            <div className="col-md-6"><LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} /></div>
                            <div className="col-md-6"><LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} /></div>
                            <div className="col-md-6"><LoadingBar height="38px" width="100%" style={{ borderRadius: '8px' }} /></div>
                        </div>
                </MyAccountCard>
            </PageShellLoading>
        );
    }

    const isEditMode = Boolean(editId);

    return (
        <MyAccountPageShell
            title="Team Management"
            subtitle="Manage team members who can access this account."
            meta={`${teamMembers.length} ${teamMembers.length === 1 ? 'team member' : 'team members'}`}
            actions={!isEditMode && canAddTeamMember ? (
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                        setError(null);
                        setSuccess(null);
                        resetForm();
                        setShowForm(true);
                    }}
                >
                    Add Team Member
                </button>
            ) : undefined}
        >
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="alert alert-success" role="alert">
                        {success}
                    </div>
                )}
                {!isEditMode && !canAddTeamMember && (
                    <p className="text-muted mb-0">
                        Adding team members is disabled.
                    </p>
                )}
                {!canDeleteTeamMember && (
                    <p className="text-muted mb-0">
                        Deleting team members is disabled.
                    </p>
                )}

                <DataTable
                    rows={teamMembers}
                    rowKey={(member) => member.id}
                    responsiveMode="stacked-cards"
                    tableClassName="table table-hover align-middle weka-team-table"
                    columns={[
                                {
                                    id: 'name',
                                    header: 'Name',
                                    mobileLabel: 'Team member',
                                    mobileDisplay: 'title',
                                    render: (member) => (
                                        <div>
                                            <div className="fw-semibold">{[member.first_name, member.last_name].filter(Boolean).join(' ') || 'Unnamed user'}</div>
                                            {member.email && <div className="text-muted small">{member.email}</div>}
                                        </div>
                                    ),
                                },
                                {
                                    id: 'phone',
                                    header: 'Phone',
                                    mobileLabel: 'Phone',
                                    mobileDisplay: 'field',
                                    render: (member) => member.phone || '—',
                                },
                                {
                                    id: 'company',
                                    header: 'Company',
                                    mobileLabel: 'Company',
                                    mobileDisplay: 'field',
                                    render: (member) => member.company || '—',
                                },
                                {
                                    id: 'role',
                                    header: 'Role',
                                    mobileLabel: 'Role',
                                    mobileDisplay: 'field',
                                    render: (member) => member.role || '—',
                                },
                                {
                                    id: 'actions',
                                    header: 'Actions',
                                    mobileDisplay: 'actions',
                                    render: (member) => (
                                        <div
                                            className="weka-team-actions"
                                            style={accountActionIconGroupStyle}
                                        >
                                            <button
                                                type="button"
                                                className="weka-team-action"
                                                onClick={() => handleEdit(member.id)}
                                                disabled={!canEditTeamMember}
                                                title="Edit"
                                                aria-label="Edit"
                                                style={{
                                                    ...accountActionIconStyle,
                                                    opacity: canEditTeamMember ? 1 : 0.45,
                                                    cursor: canEditTeamMember ? 'pointer' : 'not-allowed',
                                                }}
                                            >
                                                <i className="fas fa-pen" aria-hidden="true"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="weka-team-action"
                                                onClick={() => handleDelete(member.id, member.email)}
                                                disabled={!canDeleteTeamMember}
                                                title="Delete"
                                                aria-label="Delete"
                                                style={{
                                                    ...accountActionIconStyle,
                                                    opacity: canDeleteTeamMember ? 1 : 0.45,
                                                    cursor: canDeleteTeamMember ? 'pointer' : 'not-allowed',
                                                }}
                                            >
                                                <i className="fas fa-trash" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    ),
                                },
                    ]}
                    emptyState={<div className="weka-team-table__empty">No team members found.</div>}
                />

                {showForm && ((isEditMode && canEditTeamMember) || (!isEditMode && canAddTeamMember)) && (
                    <div
                        className="selector-modal-overlay"
                        onClick={() => {
                            if (!submitting) {
                                cancelEdit();
                            }
                        }}
                    >
                        <div
                            className="selector-modal selector-modal--team"
                            onClick={(event) => event.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="team-member-modal-title"
                        >
                            <div className="selector-modal__header">
                                <div>
                                    <div id="team-member-modal-title" className="selector-modal__title">
                                        {isEditMode ? 'Edit Team Member' : 'Add Team Member'}
                                    </div>
                                    <p className="selector-modal__subtitle">
                                        {isEditMode
                                            ? "Update this team member's account details."
                                            : 'Create a team member who can access this account.'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    aria-label="Close"
                                    disabled={submitting}
                                    onClick={cancelEdit}
                                    className={`selector-modal__close${submitting ? ' is-disabled' : ''}`}
                                >
                                    &times;
                                </button>
                            </div>

                            <form className="selector-modal__body" onSubmit={handleSubmit}>
                                {error && (
                                    <div className="selector-modal__error" role="alert">
                                        {error}
                                    </div>
                                )}

                                <div className="weka-team-form-grid">
                                    <div>
                                        <label htmlFor="team-first-name" className="form-label">First Name</label>
                                        <input
                                            id="team-first-name"
                                            type="text"
                                            name="firstName"
                                            value={formData.firstName}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control"
                                            autoFocus
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="team-last-name" className="form-label">Last Name</label>
                                        <input
                                            id="team-last-name"
                                            type="text"
                                            name="lastName"
                                            value={formData.lastName}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="team-email" className="form-label">Email</label>
                                        <input
                                            id="team-email"
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="team-phone" className="form-label">Phone</label>
                                        <input
                                            id="team-phone"
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                            className="form-control"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="team-role" className="form-label">Role</label>
                                        <select
                                            id="team-role"
                                            name="role"
                                            value={formData.role}
                                            onChange={handleInputChange}
                                            required
                                            className="form-select"
                                        >
                                            {roleOptions.map((roleName) => (
                                                <option key={roleName} value={roleName}>{roleName}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="weka-team-form-actions">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`button weka-primary-btn selector-modal__button selector-modal__button--primary${submitting ? ' is-disabled' : ''}`}
                                    >
                                        {submitting ? 'Saving...' : (isEditMode ? 'Save Team Member' : 'Add Team Member')}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={cancelEdit}
                                        disabled={submitting}
                                        className={`button weka-secondary-btn selector-modal__button selector-modal__button--secondary${submitting ? ' is-disabled' : ''}`}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
        </MyAccountPageShell>
    );
};

export default TeamManagement;
